import {parseDate} from "../../../utils/validate-date/parse-date"

export const toISODate = (value) => {
    const parsed = parseDate(value)
    if (!parsed) return null
    const yyyy = String(parsed.getFullYear()).padStart(4, "0")
    const mm = String(parsed.getMonth() + 1).padStart(2, "0")
    const dd = String(parsed.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
}

export const toInt = (value) => {
    if (value === undefined || value === null) return null
    const text = `${value}`
    if (text.includes(".") || text.includes(",")) return null
    const clean = `${value}`.replace(/\D/g, "")
    if (!clean.length) return null
    const parsed = Number(clean)
    return Number.isNaN(parsed) ? null : parsed
}

export const normalizeDecimalInput = (value) => {
    let text = typeof value === "number"
        ? value.toLocaleString("en-US", {useGrouping: false, maximumSignificantDigits: 21})
        : String(value ?? "").trim()
    if (!text) return ""
    if (/^[+-]?\d{1,3}(?:\.\d{3})+,\d+$/.test(text)) {
        text = text.replace(/\./g, "")
    }
    if (!/^[+-]?\d+(?:[.,]\d+)?$/.test(text)) throw new Error("Formato numérico inválido.")
    return text.replace(/^\+/, "").replace(",", ".")
}

export const normalizeDecimalFields = (rows, fieldLabels) =>
    rows.map((item, index) =>
        Object.fromEntries(
            Object.entries(item).map(([key, value]) => {
                if (!fieldLabels[key]) return [key, value]
                try {
                    return [key, normalizeDecimalInput(value)]
                } catch (_error) {
                    throw new Error(`Linha ${index + 2}: valor inválido em ${fieldLabels[key]}.`)
                }
            })
        )
    )

export const toDecimal = (value) => {
    if (value === undefined || value === null) return null

    const text = `${value}`.trim()
    if (!text) return null

    // troca vírgula por ponto
    const normalized = text.replace(",", ".")

    // valida formato decimal (ex: 10, 10.5, 0.10)
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null

    const parsed = Number(normalized)
    return Number.isNaN(parsed) ? null : parsed
}

export const toBigNumber = (value) => {
    if (value === undefined || value === null) return null

    const text = `${value}`.trim()
    if (!text) return null

    // troca vírgula por ponto
    const normalized = text.replace(",", ".")

    // valida formato decimal (ex: 10, 10.5, 0.10)
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null

    return normalized || null
}

export const toRadioInt = (value) => {
    if (value === "sim") return 1
    if (value === "nao") return 0
    return null
}

export const toDecimalComma = (value) => {
    if (value === undefined || value === null) return ""

    let text = `${value}`.trim()
    if (!text) return ""

    // remove tudo que não é número, ponto ou vírgula
    text = text.replace(/[^\d.,]/g, "")

    // se vier com ponto, converte para vírgula
    if (text.includes(".")) {
        text = text.replace(/\./g, ",")
    }

    // garante apenas uma vírgula
    const parts = text.split(",")
    if (parts.length > 2) {
        text = `${parts[0]},${parts.slice(1).join("")}`
    }

    return text
}

export const fromRadioLike = (value) => {
    if (value === 1 || value === "1" || value === true) return "sim"
    if (value === 0 || value === "0" || value === false) return "nao"
    return ""
}

export const ensureObject = (obj) => obj || {}
export const limitLength = (value, max) => {
    if (value === undefined || value === null) return null
    const text = `${value}`.trim()
    if (!text) return null
    return text.slice(0, max)
}
export const isValidUrl = (text) => {
    if (!text) return false
    try {
        const url = new URL(text)
        return Boolean(url.protocol && url.host)
    } catch (_e) {
        return false
    }
}
export const pickValor = (obj, key) => {
    const primary = obj?.[`${key}Valor`]
    const value = primary !== undefined ? primary : obj?.[key]
    if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === "Não Tem"
    ) {
        return null
    }
    return `${value}`
}
export const normalizeDoc = (val) => {
    const text = `${val || ""}`.replace(/\D/g, "")
    return text.length ? text : null
}

const parseISODateParts = (value) => {
    if (!value) return null
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null
        return {
            y: value.getFullYear(),
            m: value.getMonth() + 1,
            d: value.getDate(),
        }
    }
    const text = `${value}`
    const [datePart] = text.split("T")
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart)
    if (match) {
        return {
            y: Number(match[1]),
            m: Number(match[2]),
            d: Number(match[3]),
        }
    }
    const parsedBr = parseDate(text)
    if (parsedBr) {
        return {
            y: parsedBr.getFullYear(),
            m: parsedBr.getMonth() + 1,
            d: parsedBr.getDate(),
        }
    }
    return null
}

export const formatDateBr = (value) => {
    const parts = parseISODateParts(value)
    if (!parts) return ""
    const dd = String(parts.d).padStart(2, "0")
    const mm = String(parts.m).padStart(2, "0")
    return `${dd}/${mm}/${parts.y}`
}

export const ensureString = (val) => (val === undefined || val === null ? "" : `${val}`)
export const ensureUpperString = (val) => ensureString(val).toUpperCase()

export const mapGarantiasField = (garantias, key) => ensureString(garantias?.[key] ?? "")

export const pickCaseInsensitive = (obj = {}, key = "") => {
    if (!obj || typeof obj !== "object") return undefined
    if (obj[key] !== undefined) return obj[key]
    const lowerKey = key.toLowerCase()
    const foundKey = Object.keys(obj).find((k) => k.toLowerCase() === lowerKey)
    return foundKey ? obj[foundKey] : undefined
}

export const mapFrequencyToInt = (value) => {
    if (value === undefined || value === null || value === "") return null

    // se já for number válido, retorna direto
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value
    }

    const text = `${value}`.toLowerCase().trim()

    if (text === "0" || text.includes("bullet")) return 0
    if (text === "-1" || text.includes("vari")) return -1
    if (text.includes("mensal")) return 1
    if (text.includes("bimes")) return 2
    if (text.includes("trimes")) return 3
    if (text.includes("quadrimes")) return 4
    if (text.includes("semes")) return 6
    if (text.includes("anual") || text.includes("anu")) return 12

    const parsed = Number(text)
    return Number.isNaN(parsed) ? null : parsed
}
